import React from 'react';
import { HStack, Input, VStack, NumberInput, NumberInputField, Container, Spinner, IconButton, Box, } from '@chakra-ui/react';
import { CheckIcon, CloseIcon, AttachmentIcon } from '@chakra-ui/icons';
import Text from '../../../../../../components/content/text';
import COLORS from '../../../../../../constants/colors';
import Tag from '../../../../../../components/content/tag';
import { getAgendaItemTypeLabel } from '../../../../../../utils/agendaItem';
import styles from './styles';
import FONTS from '../../../../../../constants/fonts';
import { useLogic } from './logic.web';
import PopOver from '../../../../../../components/surfaces/pop-over/index.web';
import SelectAgendaItemType from '../../../../../select-agenda-item-type';
import { useDrag, useDrop } from 'react-dnd';
import { Kind } from '../../../../../../components/inputs/button/constants/kind';
import { Size } from '../../../../../../components/inputs/button/constants/size';
import LoadingAnimation from '../../../../../../components/utility/loading';
import Icon from '../../../../../../components/content/icon';
import { t } from 'i18next';
import { tKeys } from '../../../../../../translations/keys';
import Button from '../../../../../../components/inputs/button';
import { AnswerTypeEnum } from '../../../../../../enum/AnswerType';
export const AgendaItemRow = ({ item, index, parentIndex = '', onEdit, onEditPress, isNew = false, isAdmin = false, refetchAgendaItems, agendaItems, parentItem, isDraggable, handleDropAgendaItem, onVote, isVoteEnabled, isCurrentUserInvited, isCurrentUserPresent, defaultDocumentType, selectedCompanyId, eventId, }) => {
    const { isEditing, setIsEditing, tempValue, setTempValue, tempType, tempDuration, setTempDuration, displayIndex, handleSave, handleKeyDown, handleDelete, isUpdateLoading, isDeleteLoading, setTempType, answers, selectedAnswer, handlePressVote, handleMoveRight, handleMoveLeft, handleOnDrop, isFilesUploading, } = useLogic({
        item,
        index,
        parentIndex,
        onEdit,
        onEditPress,
        isNew,
        defaultDocumentType,
        selectedCompanyId,
        refetchAgendaItems,
        agendaItems,
        parentItem,
        eventId,
    });
    const [{ isDragging }, drag] = useDrag({
        type: 'agendaItem',
        item: { agendaItemId: item.id, index, parentId: item.agendaItemParentId },
        canDrag: isDraggable,
        collect: monitor => ({
            isDragging: monitor.isDragging(),
        }),
    });
    const [{ isOver, dropPosition, itemType }, drop] = useDrop({
        accept: ['agendaItem', 'Files'],
        collect: monitor => {
            if (!monitor.isOver({ shallow: true })) {
                return {
                    isOver: false,
                    dropPosition: 'none',
                    itemType: null,
                };
            }
            const itemType = monitor.getItemType();
            if (itemType === 'Files') {
                return {
                    isOver: true,
                    dropPosition: 'none',
                    itemType,
                };
            }
            const draggedItem = monitor.getItem();
            if (draggedItem.agendaItemId === item.id) {
                return {
                    isOver: false,
                    dropPosition: 'none',
                    itemType,
                };
            }
            const hoverBoundingRect = document
                .querySelector(`[data-id="${item.id}"]`)
                ?.getBoundingClientRect();
            const clientOffset = monitor.getClientOffset();
            if (!hoverBoundingRect || !clientOffset) {
                return {
                    isOver: false,
                    dropPosition: 'none',
                    itemType,
                };
            }
            const topThreshold = hoverBoundingRect.top + hoverBoundingRect.height * 0.25;
            const bottomThreshold = hoverBoundingRect.bottom - hoverBoundingRect.height * 0.25;
            let dropPosition = 'none';
            if (clientOffset.y < topThreshold) {
                dropPosition = 'above';
            }
            else if (clientOffset.y > bottomThreshold) {
                dropPosition = 'below';
            }
            return {
                isOver: true,
                dropPosition,
                itemType,
            };
        },
        drop: (draggedItem, monitor) => {
            if (!monitor.isOver({ shallow: true }))
                return;
            if (monitor.getItemType() === 'Files') {
                if (draggedItem.files) {
                    handleOnDrop(draggedItem.files);
                }
                return;
            }
            const hoverBoundingRect = document
                .querySelector(`[data-id="${item.id}"]`)
                ?.getBoundingClientRect();
            const clientOffset = monitor.getClientOffset();
            if (!hoverBoundingRect || !clientOffset)
                return;
            const topThreshold = hoverBoundingRect.top + hoverBoundingRect.height * 0.25;
            const bottomThreshold = hoverBoundingRect.bottom - hoverBoundingRect.height * 0.25;
            let newIndex = index;
            let isBottom = false;
            if (clientOffset.y < topThreshold) {
                newIndex = index;
                isBottom = false;
            }
            else if (clientOffset.y > bottomThreshold) {
                newIndex = index + 1;
                isBottom = true;
            }
            void handleDropAgendaItem(draggedItem.agendaItemId, item.agendaItemParentId, newIndex, isBottom);
        },
    });
    const dragDropRef = (node) => {
        drag(drop(node));
    };
    const [isFileDropping, setIsFileDropping] = React.useState(false);
    const handleDragOver = React.useCallback((e) => {
        if (!e.dataTransfer.types.includes('Files'))
            return;
        e.preventDefault();
        e.stopPropagation();
        setIsFileDropping(true);
    }, []);
    const handleDragLeave = React.useCallback((e) => {
        if (!e.dataTransfer.types.includes('Files'))
            return;
        e.preventDefault();
        e.stopPropagation();
        setIsFileDropping(false);
    }, []);
    const handleNativeDrop = React.useCallback((e) => {
        if (!e.dataTransfer.types.includes('Files'))
            return;
        e.preventDefault();
        e.stopPropagation();
        setIsFileDropping(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            handleOnDrop(files);
        }
    }, [handleOnDrop]);
    const renderEditMode = () => {
        return (<HStack width="100%" justify="space-between" py={1} px={4} bg="white" borderRadius="md" boxShadow="sm">
        <HStack spacing={1} flex={1}>
          <Text color="gray.400" fontSize="sm" minWidth="32px" textAlign="left">
            {displayIndex}
          </Text>
          <Container pl={0}>
            <Input value={tempValue} onChange={e => setTempValue(e.target.value)} onKeyDown={handleKeyDown} isDisabled={isUpdateLoading} flex={1} border="none" bg="transparent" fontSize={parentItem
                ? FONTS.T1.T12Px.Regular400.value.fontSize
                : FONTS.T1.T16Px.Regular400.value.fontSize} fontWeight={parentItem
                ? FONTS.T1.T12Px.Regular400.value.fontWeight
                : FONTS.T1.T16Px.Regular400.value.fontWeight} _focus={{
                boxShadow: 'none',
                border: 'none',
            }}/>
          </Container>
        </HStack>

        <HStack spacing={3}>
          <IconButton aria-label="Attach document" icon={<>
                <AttachmentIcon boxSize={3}/>
                {item.filesCount && item.filesCount > 0 && (<Text position="absolute" top="-2px" right="-2px" fontSize="10px" bg="blue.500" color="white" borderRadius="full" width="14px" height="14px">
                    {item.filesCount}
                  </Text>)}
              </>} size="xs" variant="ghost" colorScheme="gray" onClick={() => onEditPress(item)}/>
          <NumberInput value={tempDuration} onChange={value => setTempDuration(parseInt(value) || 0)} min={0} width="2rem" isDisabled={isUpdateLoading} size="sm">
            <NumberInputField placeholder="0" bg="gray.100" _hover={{ bg: 'gray.200' }} border="none" px={'0.5rem'} py={'0.25rem'} fontSize={FONTS.T1.T12Px.Regular400.value.fontSize} fontWeight={FONTS.T1.T12Px.Regular400.value.fontWeight} color={COLORS.Text.T400.value}/>
          </NumberInput>

          <PopOver from={<Tag backgroundColor={COLORS.Govrn.Blue.T100.value} color={COLORS.Govrn.Blue.T500.value} style={styles.agendaItemTypeTag} hasEndEnhancer={true}>
                {getAgendaItemTypeLabel(tempType)}
              </Tag>} placement="bottom-end">
            <SelectAgendaItemType agendaItemType={tempType} onChange={type => setTempType(type)}/>
          </PopOver>

          {isUpdateLoading ? (<Spinner size="sm" color="blue.500"/>) : (<HStack spacing={1}>
              <IconButton aria-label="Save" icon={<CheckIcon boxSize={3}/>} size="xs" colorScheme="green" onClick={handleSave} isDisabled={!tempValue.trim()}/>
              <IconButton aria-label="Cancel" icon={<CloseIcon boxSize={3}/>} size="xs" colorScheme="red" variant="ghost" onClick={() => setIsEditing(false)}/>
            </HStack>)}
        </HStack>
      </HStack>);
    };
    return (<Box ref={dragDropRef} data-id={item.id} opacity={isDragging ? 0.5 : 1} cursor={isDraggable ? 'grab' : 'default'} position="relative" width="100%" onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleNativeDrop} _before={{
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            top: dropPosition === 'above' ? '-2px' : 'auto',
            bottom: dropPosition === 'below' ? '-2px' : 'auto',
            height: isOver && dropPosition !== 'none' ? '2px' : '0',
            backgroundColor: isOver && dropPosition !== 'none'
                ? COLORS.Govrn.Blue.T500.value
                : 'transparent',
            zIndex: 2,
        }} _after={{
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            backgroundColor: (isOver && itemType === 'Files') || isFileDropping
                ? 'rgba(66, 153, 225, 0.1)'
                : 'transparent',
            border: (isOver && itemType === 'Files') || isFileDropping
                ? `2px dashed ${COLORS.Govrn.Blue.T500.value}`
                : 'none',
            borderRadius: 'md',
            zIndex: 1,
            pointerEvents: 'none',
        }}>
      {isFilesUploading && (<Box position="absolute" top={0} left={0} right={0} bottom={0} bg="rgba(255, 255, 255, 0.7)" zIndex={2} display="flex" alignItems="center" justifyContent="center" borderRadius="md">
          <HStack spacing={3}>
            <Spinner size="sm" color="blue.500"/>
            <Text color="blue.500" fontSize="sm" fontWeight="medium">
              {t(tKeys.common.uploading_files)}
            </Text>
          </HStack>
        </Box>)}
      {isFileDropping && (<HStack width="100%" justify="center" align="center" height="100%" position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" zIndex={2} pointerEvents="none">
          <Icon name="picture-blue"/>
          <Text color="blue.500" fontSize="sm" fontWeight="medium">
            Drop the files here to attach them to the agenda item
          </Text>
        </HStack>)}
      <VStack width="100%" spacing={0} align="stretch">
        {isEditing ? (renderEditMode()) : (<HStack width="100%" justify="space-between" pt={item.agendaItemParentId ? 1 : '1rem'} pb={1} px={4} bg={!item.agendaItemParentId
                ? `rgba(${COLORS.BG.value}, ${0.1 * item.level})`
                : 'transparent'} pl={4 + (!item.agendaItemParentId ? 0 : 15)} cursor={isUpdateLoading || !isAdmin ? 'default' : 'pointer'} borderLeft={!item.agendaItemParentId
                ? `2px solid ${COLORS.Govrn.Blue.T200.value}`
                : 'none'} _hover={{
                bg: !isUpdateLoading && isAdmin ? COLORS.BG.value : undefined,
                borderLeft: !item.agendaItemParentId
                    ? `2px solid ${COLORS.Govrn.Blue.T500.value}`
                    : 'none',
                '& .delete-button': {
                    opacity: 1,
                },
                '& .hierarchy-buttons': {
                    opacity: 1,
                },
            }} transition="all 0.2s" opacity={isUpdateLoading ? 0.6 : 1} pointerEvents={isUpdateLoading ? 'none' : 'auto'} onClick={() => !isUpdateLoading && isAdmin && setIsEditing(true)}>
            <HStack width="100%" justify="space-between">
              <HStack spacing={1} flex={1}>
                <Text color={!item.agendaItemParentId ? 'gray.700' : 'gray.500'} font={!item.agendaItemParentId
                ? FONTS.T1.T14Px.SemiBold600.value
                : FONTS.T1.T12Px.Regular400.value} minWidth="32px">
                  {displayIndex}
                </Text>
                <Container pl={2}>
                  <Text numberOfLines={3} style={{
                flex: 1,
                fontSize: item.agendaItemParentId ? '12px' : '16px',
                fontWeight: item.agendaItemParentId ? '400' : '600',
            }}>
                    {item.title}
                  </Text>
                </Container>
              </HStack>

              <HStack spacing={3} alignItems="center">
                {isUpdateLoading && (<HStack spacing={2}>
                    <Spinner size="sm" color={COLORS.Govrn.Blue.T500.value}/>
                    <Text color="gray.500" fontSize="sm">
                      Updating...
                    </Text>
                  </HStack>)}

                {isAdmin && !isUpdateLoading && (<HStack spacing={1} className="hierarchy-buttons" opacity={0} transition="opacity 0.2s">
                    <IconButton aria-label="Decrease indent" icon={<Icon name="chevron-left" width={16} height={16}/>} size="xs" variant="ghost" colorScheme="gray" onClick={e => {
                    e.stopPropagation();
                    handleMoveLeft();
                }} isDisabled={!item.agendaItemParentId}/>
                    <IconButton aria-label="Increase indent" icon={<Icon name="chevron-right" width={16} height={16}/>} size="xs" variant="ghost" colorScheme="gray" onClick={e => {
                    e.stopPropagation();
                    handleMoveRight();
                }} isDisabled={index === 0 || !agendaItems?.[index - 1]}/>
                  </HStack>)}

                <IconButton aria-label="Attach document" icon={<>
                      <AttachmentIcon boxSize={3}/>
                      {item.filesCount > 0 && (<Text position="absolute" top="-2px" right="-2px" fontSize="10px" bg="blue.500" color="white" borderRadius="full" width="14px" height="14px">
                          {item.filesCount}
                        </Text>)}
                    </>} size="xs" variant="ghost" colorScheme="gray" onClick={e => {
                e.stopPropagation();
                onEditPress(item);
            }}/>
                <Text color="gray.500">{item.duration}m</Text>
                <Tag backgroundColor={COLORS.Govrn.Blue.T100.value} color={COLORS.Govrn.Blue.T500.value} style={styles.agendaItemTypeTag}>
                  {getAgendaItemTypeLabel(item.type)}
                </Tag>
                {isAdmin && (<Box onClick={e => e.stopPropagation()} p={1} borderRadius="md" _hover={{ bg: 'gray.100' }} transition="background-color 0.2s">
                    <PopOver from={<Icon name="more-vertical" height={20} width={20} style={{ cursor: 'pointer' }}/>} placement="bottom-start">
                      <VStack width={'full'} alignItems={'flex-start'} minW={'12rem'} spacing={0} py={'.5rem'}>
                        <Button onPress={() => onEditPress(item)} kind={Kind.TRANSPARENT} width={'full'} size={Size.COMPACT}>
                          <HStack width={'full'}>
                            <Icon name="edit" width={30} height={30} fill={'transparent'}/>
                            <Text font={FONTS.T1.T14Px.Medium500.value}>
                              {isAdmin
                    ? t(tKeys.news_feed.edit_post)
                    : t(tKeys.tasks.open)}
                            </Text>
                          </HStack>
                        </Button>

                        {isAdmin && (<Button onPress={async () => await (!isDeleteLoading && handleDelete())} kind={Kind.TRANSPARENT} width={'full'} size={Size.COMPACT} needConfirmation={true}>
                            <HStack width={'full'}>
                              {isDeleteLoading ? (<LoadingAnimation size={Size.MINI}/>) : (<Icon name="delete-bin" width={30} height={30}/>)}
                              <Text font={FONTS.T1.T14Px.Medium500.value} color={isDeleteLoading
                        ? COLORS.InputText.value
                        : COLORS.Text.T800.value}>
                                {t(tKeys.news_feed.delete_post.title)}
                              </Text>
                            </HStack>
                          </Button>)}
                      </VStack>
                    </PopOver>
                  </Box>)}
              </HStack>
            </HStack>
          </HStack>)}
        {/* Question and Vote section */}
        {item.question && (<Box mt={2} ml={8} p={3} bg="gray.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="#AA55E3">
            <VStack align="stretch" spacing={3}>
              <HStack>
                <Text font={FONTS.T1.T14Px.Medium500.value} color={COLORS.Text.T700.value} maxWidth="600px">
                  {item.question.text}
                </Text>
              </HStack>

              {/* Vote results */}
              <HStack spacing={4}>
                <Button kind={Kind.TRANSPARENT} size={Size.COMPACT} onPress={() => {
                if (isCurrentUserInvited &&
                    isCurrentUserPresent &&
                    item.id) {
                    handlePressVote({
                        id: item.question?.id,
                        answerId: item.question?.answers?.[0]?.id,
                        type: AnswerTypeEnum.Yes,
                    });
                }
            }} isDisabled={!isCurrentUserInvited || !isCurrentUserPresent}>
                  <HStack spacing={2} p={2} bg={selectedAnswer === 'Yes' ? '#E9D6F6' : 'white'} borderRadius="md" boxShadow="sm">
                    <Icon name="check" width={18} height={18} color={selectedAnswer === 'Yes'
                ? '#AA55E3'
                : COLORS.Text.T400.value}/>
                    <Text font={FONTS.T1.T14Px.Medium500.value} color={selectedAnswer === 'Yes'
                ? '#AA55E3'
                : COLORS.Text.T600.value}>
                      {item.question.answers?.[0]?.votes?.length || 0}{' '}
                      {t(tKeys.common.yes)}
                    </Text>
                  </HStack>
                </Button>

                <Button kind={Kind.TRANSPARENT} size={Size.COMPACT} onPress={() => {
                handlePressVote({
                    id: item.question?.id,
                    answerId: item.question?.answers?.[1]?.id,
                    type: AnswerTypeEnum.No,
                });
            }} isDisabled={!isCurrentUserInvited || !isCurrentUserPresent}>
                  <HStack spacing={2} p={2} bg={selectedAnswer === 'No' ? '#E9D6F6' : 'white'} borderRadius="md" boxShadow="sm">
                    <Icon name="cross" width={18} height={18} fill={selectedAnswer === 'No'
                ? '#AA55E3'
                : COLORS.Text.T400.value}/>
                    <Text font={FONTS.T1.T14Px.Medium500.value} color={selectedAnswer === 'No'
                ? '#AA55E3'
                : COLORS.Text.T600.value}>
                      {item.question.answers?.[1]?.votes?.length || 0}{' '}
                      {t(tKeys.common.no)}
                    </Text>
                  </HStack>
                </Button>

                <Button kind={Kind.TRANSPARENT} size={Size.COMPACT} onPress={() => {
                handlePressVote({
                    id: item.question?.id,
                    answerId: item.question?.answers?.[2]?.id,
                    type: AnswerTypeEnum.Abstain,
                });
            }} isDisabled={!isCurrentUserInvited || !isCurrentUserPresent}>
                  <HStack spacing={2} p={2} bg={selectedAnswer === 'Abstain' ? '#E9D6F6' : 'white'} borderRadius="md" boxShadow="sm">
                    <Icon name="forbidden" width={18} height={18} fill={selectedAnswer === 'Abstain'
                ? '#AA55E3'
                : COLORS.Text.T400.value} stroke={selectedAnswer === 'Abstain'
                ? '#AA55E3'
                : COLORS.Text.T400.value}/>
                    <Text font={FONTS.T1.T14Px.Medium500.value} color={selectedAnswer === 'Abstain'
                ? '#AA55E3'
                : COLORS.Text.T600.value}>
                      {item.question.answers?.[2]?.votes?.length || 0}{' '}
                      {t(tKeys.common.abstain)}
                    </Text>
                  </HStack>
                </Button>

                {!item.question.isAnonymous && (<Button kind={Kind.TRANSPARENT} size={Size.COMPACT} onPress={() => onVote(item?.question, false)}>
                    <HStack spacing={2} p={2}>
                      <Text font={FONTS.T1.T12Px.Medium500.value} color={COLORS.Govrn.Blue.T500.value}>
                        {t(tKeys.calendar.agenda.item.seeResults)}
                      </Text>
                      <Icon name="chevron-right" width={18} height={18} fill={COLORS.Text.T400.value}/>
                    </HStack>
                  </Button>)}
              </HStack>
            </VStack>
          </Box>)}
        <VStack spacing={1} width="100%" align="stretch">
          {item.subAgendaItems?.map((child, childIndex) => (<AgendaItemRow key={child.id} item={child} parentItem={item} index={childIndex} parentIndex={displayIndex} onEdit={onEdit} onEditPress={onEditPress} agendaItems={item.subAgendaItems} isAdmin={isAdmin} refetchAgendaItems={refetchAgendaItems} isDraggable={isDraggable} handleDropAgendaItem={handleDropAgendaItem} onVote={onVote} isCurrentUserInvited={isCurrentUserInvited} isCurrentUserPresent={isCurrentUserPresent} defaultDocumentType={defaultDocumentType} selectedCompanyId={selectedCompanyId} eventId={eventId}/>))}
        </VStack>
      </VStack>
    </Box>);
};
